import * as React from 'react'
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { formatDate } from 'date-fns'
import {
  ArrowPathRoundedSquareIcon,
  ChevronRightIcon,
  ListBulletIcon,
  PlusCircleIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui/skeleton'
import useCloseToOrigin from '@/hooks/useCloseToOrigin'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import useGetPeriodByOffset from '@/services/api/cache/useGetPeriodByOffset'
import useGetVault from '@/services/api/vault/useGetVault'
import useGetTransactionCategory from '@/services/api/transaction-category/useGetTransactionCategory'
import useGetCellTransactions from '@/services/api/cash-flow/transaction/useGetCellTransactions'
import useVaultBalanceForOffset from '@/services/api/cash-flow/useVaultBalanceForOffset'
import { ActionDropdown } from '@/components/transactions/TransactionListTable'
import {
  TRANSACTION_DIRECTION,
  TRANSACTION_TYPES,
} from '@/data/constants/constants'

const EDIT_ROUTE = /\/transactions\/[^/]+\/edit$/

/**
 * Phone / tablet stand-in for TransactionListModal: the "List" tab of the
 * transaction form. Same routes and data as the desktop list, laid out as a
 * single column that fits the sheet. Editing a row renders the nested form
 * route in its place.
 */
export default function MobileTransactionList() {
  const { categoryId, vaultId } = useParams()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const closeToOrigin = useCloseToOrigin()
  const formatter = useNumberFormatter({})

  const offset = searchParams.get('offset')
  const isVault = !!vaultId
  const entityId = Number(vaultId ?? categoryId)

  const { data: vault } = useGetVault({ vaultId })
  const { data: category } = useGetTransactionCategory({ id: categoryId })
  const { data: period, status: periodStatus } = useGetPeriodByOffset({
    offset,
  })
  const { data: transactions, isLoading } = useGetCellTransactions({
    entityType: isVault ? 'vaults' : 'transaction_categories',
    entityId,
    start: period?.start,
    end: period?.end,
    enabled: periodStatus === 'success',
  })
  const { data: balances } = useVaultBalanceForOffset({
    offset: offset !== null ? Number(offset) : undefined,
    vaultId: isVault ? entityId : undefined,
  })

  if (EDIT_ROUTE.test(location.pathname)) return <Outlet />

  const type = isVault ? 'transfer' : category?.direction === 1 ? 'in' : 'out'
  const base = isVault ? `vaults/${vaultId}` : `categories/${categoryId}`

  const goNew = () =>
    navigate({
      pathname: `/${base}/transactions/new`,
      search: new URLSearchParams({ offset: offset ?? '0', type }).toString(),
    })

  const goEdit = (t: any) => {
    const p = new URLSearchParams(searchParams)
    p.set('editSeries', 'false')
    p.set(
      'type',
      t.transaction_type === TRANSACTION_TYPES.TRANSFER
        ? 'transfer'
        : t.destination_type === TRANSACTION_DIRECTION.MONEY_IN
          ? 'in'
          : 'out',
    )
    navigate({
      pathname: `/transaction-list/${base}/transactions/${t.id}/edit`,
      search: p.toString(),
    })
  }

  // Signed from the entity's point of view — same rules as the desktop
  // list's In/Out columns and footer totals.
  const signedAmount = (t: any) => {
    const amt = Number(t.amount)
    if (isVault) {
      if (t.transaction_type === TRANSACTION_TYPES.TRANSFER)
        return t.vault_id === t.vault_to ? Math.abs(amt) : -Math.abs(amt)
      return amt
    }
    return t.is_refund ? -Math.abs(amt) : Math.abs(amt)
  }

  const counterparty = (t: any) => {
    if (t.transaction_type === TRANSACTION_TYPES.TRANSFER)
      return t.vault_id === t.vault_from
        ? t.to?.entity_name
        : t.from?.entity_name
    return isVault ? t.destination_name : t.vault_name
  }

  const list = transactions ?? []
  const total = list.reduce((acc, t) => acc + signedAmount(t), 0)
  const title = (isVault ? vault?.name : category?.name) ?? ''

  return (
    <div className="absolute inset-0 flex flex-col bg-white">
      <div className="flex shrink-0 items-start gap-2 px-4 pb-2.5 pt-3">
        <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
          <div className="truncate text-base font-semibold leading-tight text-txt-primary">
            {title || <Skeleton className="h-5 w-40" />}
          </div>
          {period && (
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-gray-tr-12 px-1.5 py-0.5 text-xs text-txt-primary">
                {period.number}
              </span>
              <span className="text-[13px] text-txt-secondary">
                {formatDate(new Date(period.start), 'd MMM')} —{' '}
                {formatDate(new Date(period.end), 'd MMM yyyy')}
              </span>
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          iconOnly
          size="sm"
          aria-label="Close"
          className="size-7"
          onClick={closeToOrigin}
        >
          <XMarkIcon />
        </Button>
      </div>

      <div className="shrink-0 px-4 pb-3">
        <div className="inline-flex gap-0.5 rounded-lg bg-gray-low p-[3px]">
          <button
            type="button"
            onClick={goNew}
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium text-txt-secondary"
          >
            <PlusCircleIcon className="size-3.5" />
            New
          </button>
          <span className="flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-[13px] font-medium text-txt-primary shadow-sm">
            <ListBulletIcon className="size-3.5" />
            List
          </span>
        </div>
      </div>

      <div className="pf-mobile-scroll min-h-0 flex-1 overflow-y-auto border-t border-light px-4 pb-4 pt-1">
        {isVault && balances && (
          <div className="flex items-center justify-between py-2.5 text-xs text-txt-secondary">
            <span>Opening balance</span>
            <span className="tabular-nums">
              {formatter(balances.opening_balance)}
            </span>
          </div>
        )}

        {isLoading || periodStatus !== 'success' ? (
          <div className="flex flex-col gap-3 pt-3">
            {[1, 2, 3, 4].map((x) => (
              <Skeleton key={x} className="h-10 w-full" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <p className="py-8 text-center text-sm text-txt-placeholder">
            Nothing logged in this period yet.
          </p>
        ) : (
          list.map((t) => {
            const isBalance = t.transaction_type === TRANSACTION_TYPES.BALANCE
            const estimateOnly = t.behaviour === 'EstimateOnly'
            const amount = signedAmount(t)
            return (
              <div
                key={t.id}
                className={`flex items-center gap-2 border-b border-light py-3 ${t.is_shadow ? 'text-txt-secondary line-through opacity-60' : ''}`}
              >
                <button
                  type="button"
                  disabled={isBalance}
                  onClick={() => goEdit(t)}
                  className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left disabled:cursor-default"
                >
                  <span className="flex min-w-0 flex-col gap-[3px]">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-txt-primary">
                        {t.description || counterparty(t) || '—'}
                      </span>
                      {t.series_id && (
                        <ArrowPathRoundedSquareIcon className="size-3 shrink-0 text-txt-placeholder" />
                      )}
                    </span>
                    <span className="truncate text-xs text-txt-secondary">
                      {[
                        formatDate(new Date(t.transaction_date), 'EEE d MMM'),
                        // Already the title when there's no description
                        t.description && counterparty(t),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    {estimateOnly && (
                      <Badge variant="outline" className="rounded-md">
                        Estimate
                      </Badge>
                    )}
                    <span className="text-sm font-semibold tabular-nums text-txt-primary">
                      {formatter(amount)}
                      {t.is_refund && (
                        <span className="ml-0.5 text-xs text-txt-secondary">*</span>
                      )}
                    </span>
                    {!isBalance && (
                      <ChevronRightIcon className="size-3.5 text-txt-placeholder" />
                    )}
                  </span>
                </button>
                {!isBalance && <ActionDropdown transaction={t} />}
              </div>
            )
          })
        )}

        {list.length > 0 && (
          <div className="flex items-center justify-between pt-3">
            <span className="text-xs font-semibold tracking-[.06em] text-txt-placeholder">
              {isVault ? 'NET' : 'TOTAL'}
            </span>
            <span className="text-[15px] font-bold tabular-nums text-txt-primary">
              {formatter(total)}
            </span>
          </div>
        )}
        {isVault && balances && (
          <div className="flex items-center justify-between pt-2 text-xs text-txt-secondary">
            <span>Closing balance</span>
            <span className="tabular-nums">
              {formatter(balances.closing_balance)}
            </span>
          </div>
        )}
      </div>

      <div className="flex shrink-0 gap-2 border-t border-light px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="flex-1 rounded-lg"
          onClick={closeToOrigin}
        >
          Close
        </Button>
        <Button
          type="button"
          size="lg"
          className="flex-1 rounded-lg"
          onClick={goNew}
        >
          Add transaction
        </Button>
      </div>
    </div>
  )
}
